/**
 * Tuning constants & catalogs.
 *
 * Every number here is anchored to real bed-in-a-box industry data
 * (2014–2026) and documented inline. Adjust via this file only — engines
 * must not hard-code economics.
 */

import type {
  ArchetypeId,
  CountryCode,
  Difficulty,
  EventCategory,
  MarketingChannelId,
  ResearchNode,
  SupplierDef,
  TeamHire,
} from './types';

// ---------------------------------------------------------------------------
// Time
// ---------------------------------------------------------------------------

export const DAYS_PER_QUARTER = 91;

// ---------------------------------------------------------------------------
// Difficulty presets — alter starting capital, competitive pressure, CAC,
// oil volatility, event severity, and credit access.
// ---------------------------------------------------------------------------

export interface DifficultyPreset {
  label: string;
  blurb: string;
  startingCash: number;
  competitionBase: number;
  cacMult: number;
  oilVolatility: number;
  eventSeverityMult: number;
  creditLimit: number;
  demandMult: number;
}

export const DIFFICULTIES: Record<Difficulty, DifficultyPreset> = {
  easy: {
    label: 'Seed Round',
    blurb: 'Generous VCs, forgiving market. Learn the ropes.',
    startingCash: 300_000,
    competitionBase: 0.85,
    cacMult: 0.8,
    oilVolatility: 0.7,
    eventSeverityMult: 0.75,
    creditLimit: 150_000,
    demandMult: 1.25,
  },
  normal: {
    label: 'Series A Grind',
    blurb: 'The realistic DTC experience: tight margins, loud competitors.',
    startingCash: 175_000,
    competitionBase: 1.0,
    cacMult: 1.0,
    oilVolatility: 1.0,
    eventSeverityMult: 1.0,
    creditLimit: 100_000,
    demandMult: 1.0,
  },
  hard: {
    label: 'Bootstrapped',
    blurb: 'Thin cash, brutal CAC, volatile oil. Survivors only.',
    startingCash: 90_000,
    competitionBase: 1.25,
    cacMult: 1.2,
    oilVolatility: 1.3,
    eventSeverityMult: 1.2,
    creditLimit: 60_000,
    demandMult: 0.9,
  },
  realistic: {
    label: '2018 Casper Simulator',
    blurb: 'Growth-at-all-costs graveyard. CAC inflation, tariff whiplash, 15% returns.',
    startingCash: 120_000,
    competitionBase: 1.5,
    cacMult: 1.4,
    oilVolatility: 1.45,
    eventSeverityMult: 1.35,
    creditLimit: 80_000,
    demandMult: 1.0,
  },
};

// ---------------------------------------------------------------------------
// Tariffs — effective landed-cost multipliers per origin (Jan 2024 baseline).
//
// Real-world anchors:
// - China: Section 301 layers + AD/CVD orders on Chinese mattresses (the
//   "all others" CVD rate alone has been ~97.78%, with sunset reviews running
//   through 2025–2026). Effective duty burden modeled at ~+92% on cost.
// - Vietnam/others: subject to AD investigations with high potential rates;
//   circumvention scrutiny (false-origin schemes) keeps a risk premium.
// - Mexico/US: USMCA / domestic, minimal duty exposure.
// Events move these numbers dynamically (spikes of +20% to +100%+).
// ---------------------------------------------------------------------------

export const INITIAL_TARIFFS: Record<CountryCode, number> = {
  CN: 1.92,
  VN: 1.18,
  ID: 1.12,
  MX: 1.03,
  US: 1.0,
};

export const TARIFF_LABELS: Record<CountryCode, string> = {
  CN: 'China',
  VN: 'Vietnam',
  ID: 'Indonesia',
  MX: 'Mexico',
  US: 'United States',
};

// ---------------------------------------------------------------------------
// Suppliers. Factory-cost multiplier is vs China ex-works baseline (=1.0).
// Lead times include ocean transit for Asia origins; Mexico/US are nearshore.
// ---------------------------------------------------------------------------

export const SUPPLIERS: SupplierDef[] = [
  {
    id: 'foshan-foamworks',
    name: 'Foshan FoamWorks',
    country: 'CN',
    countryName: 'China',
    costMultiplier: 1.0,
    leadTimeDays: 38,
    reliability: 0.92,
    qualityVariance: 0.03,
    capacityUnitsPerDay: 400,
    minOrderUnits: 150,
    tariffRisk: 1.0,
    blurb: 'Cheapest landed cost on paper — until the next AD/CVD headline.',
  },
  {
    id: 'binh-duong-sleep',
    name: 'Binh Duong Sleep Co.',
    country: 'VN',
    countryName: 'Vietnam',
    costMultiplier: 1.09,
    leadTimeDays: 42,
    reliability: 0.88,
    qualityVariance: 0.04,
    capacityUnitsPerDay: 300,
    minOrderUnits: 200,
    tariffRisk: 0.55,
    blurb: 'The post-2019 migration destination. Watch AD petition risk.',
  },
  {
    id: 'surabaya-comfort',
    name: 'Surabaya Comfort Industries',
    country: 'ID',
    countryName: 'Indonesia',
    costMultiplier: 1.14,
    leadTimeDays: 47,
    reliability: 0.84,
    qualityVariance: 0.05,
    capacityUnitsPerDay: 240,
    minOrderUnits: 250,
    tariffRisk: 0.45,
    blurb: 'Diversification play; longer lead times, greener labor costs.',
    requiresResearch: 'global-sourcing',
  },
  {
    id: 'monterrey-colchones',
    name: 'Monterrey Colchones SA',
    country: 'MX',
    countryName: 'Mexico',
    costMultiplier: 1.28,
    leadTimeDays: 13,
    reliability: 0.93,
    qualityVariance: 0.025,
    capacityUnitsPerDay: 180,
    minOrderUnits: 100,
    tariffRisk: 0.15,
    blurb: 'Nearshore fast lane: restock in under two weeks, dodge ocean freight.',
    requiresResearch: 'nearshore-lane',
  },
  {
    id: 'georgia-mattress-co',
    name: 'Georgia Mattress Works',
    country: 'US',
    countryName: 'United States',
    costMultiplier: 1.62,
    leadTimeDays: 6,
    reliability: 0.97,
    qualityVariance: 0.01,
    capacityUnitsPerDay: 140,
    minOrderUnits: 80,
    tariffRisk: 0.02,
    blurb: '"Made in USA" badge, zero trade risk, painful unit cost.',
    requiresResearch: 'reshore-pilot',
  },
];

// ---------------------------------------------------------------------------
// Product archetypes. Queen-reference pricing reflects 2024–2026 market:
// budget all-foam $200–600, mid-tier $700–1,200, premium $1,200–2,000+.
// `fairValue` = what the "market" considers normal — price above it at your
// peril unless quality/reviews justify it.
// ---------------------------------------------------------------------------

export interface ArchetypeDef {
  id: ArchetypeId;
  name: string;
  fairValue: number; // queen reference market fair price
  minQualityCost: number; // factory cost @ quality 40 (China reference)
  maxQualityCost: number; // factory cost @ quality 95
  devDays: number;
  devCost: number; // one-time sampling/cert tooling
  returnRateBase: number; // at average quality
  coolingBase: number;
  requiresResearch?: string;
  blurb: string;
}

export const ARCHETYPES: ArchetypeDef[] = [
  {
    id: 'allFoam',
    name: 'All-Foam',
    fairValue: 549,
    minQualityCost: 118,
    maxQualityCost: 238,
    devDays: 30,
    devCost: 12_000,
    returnRateBase: 0.12,
    coolingBase: 35,
    blurb: 'The classic bed-in-a-box. Commodity end of town.',
  },
  {
    id: 'hybrid',
    name: 'Hybrid (pocket coils)',
    fairValue: 899,
    minQualityCost: 172,
    maxQualityCost: 330,
    devDays: 45,
    devCost: 28_000,
    returnRateBase: 0.11,
    coolingBase: 55,
    requiresResearch: 'hybrid-platform',
    blurb: 'Coils + foam. Better sleep scores, heavier boxes, steel exposure.',
  },
  {
    id: 'coolingHybrid',
    name: 'Cooling Hybrid',
    fairValue: 1149,
    minQualityCost: 205,
    maxQualityCost: 392,
    devDays: 55,
    devCost: 42_000,
    returnRateBase: 0.10,
    coolingBase: 82,
    requiresResearch: 'cooling-line',
    blurb: 'Gel infusions + phase-change covers. Premium margins if marketed right.',
  },
  {
    id: 'latex',
    name: 'Natural Latex',
    fairValue: 1399,
    minQualityCost: 268,
    maxQualityCost: 480,
    devDays: 60,
    devCost: 55_000,
    returnRateBase: 0.085,
    coolingBase: 68,
    requiresResearch: 'latex-line',
    blurb: 'Eco-conscious segment, low returns, latex price volatility risk.',
  },
  {
    id: 'luxury',
    name: 'Luxury Pillow-Top Hybrid',
    fairValue: 1899,
    minQualityCost: 340,
    maxQualityCost: 610,
    devDays: 75,
    devCost: 90_000,
    returnRateBase: 0.09,
    coolingBase: 74,
    requiresResearch: 'luxury-line',
    blurb: 'Flagship halo product. Showrooms love it. So do copycats.',
  },
];

/** Size mix (queen-equivalent demand weighting). Blended ASP ≈ price × 0.98. */
export const SIZE_MIX_ASP_FACTOR = 0.98;

// ---------------------------------------------------------------------------
// Marketing channels — base CAC per acquired customer (queen-scale blended).
//
// Real-world anchor: blended DTC mattress CAC ran ~$120–$220 by 2018–2023.
// BALANCE NOTE (v2 tuning pass): bases were lowered ~10–12% from the first
// pass — analysis showed the starter SKU's contribution margin (~$65/unit
// after parcel+fees on tariffed China goods) made *every* paid channel
// permanently underwater, with no reachable path to profitability before
// research. The lesson "unit economics before scale" is preserved (starter
// SKU ads still lose money; hybrid/premium SKUs flip strongly positive),
// but a competent player can now cross into paid-acquisition payback within
// the first year instead of never.
// brandBoost values were raised ~3.5x from the first pass: awareness is the
// game's core growth flywheel and was projected to take 400+ days of heavy
// spend to reach 50 points — dead pacing. Now ~2–3 months of sustained
// spend gets you there, matching how DTC brands actually build brands.
// ---------------------------------------------------------------------------

export interface MarketingChannelDef {
  id: MarketingChannelId;
  name: string;
  baseCac: number;
  saturation: number; // daily spend where tanh saturation bites (~half effect)
  fatiguePerDay: number; // fatigue growth at full spend
  recoveryPerDay: number;
  brandBoost: number; // awareness points per $1k spend
  notes: string;
}

export const MARKETING_CHANNELS: MarketingChannelDef[] = [
  {
    id: 'meta',
    name: 'Meta Ads',
    baseCac: 132,
    saturation: 900,
    fatiguePerDay: 0.010,
    recoveryPerDay: 0.05,
    brandBoost: 0.034,
    notes: 'Workhorse for DTC. Fatigues fast; refresh creative or watch CPA climb.',
  },
  {
    id: 'google',
    name: 'Google Search / PMax',
    baseCac: 152,
    saturation: 1100,
    fatiguePerDay: 0.004,
    recoveryPerDay: 0.07,
    brandBoost: 0.020,
    notes: 'High-intent "best mattress" queries. Auction inflates during Q4.',
  },
  {
    id: 'tiktok',
    name: 'TikTok / Reels',
    baseCac: 106,
    saturation: 800,
    fatiguePerDay: 0.014,
    recoveryPerDay: 0.06,
    brandBoost: 0.052,
    notes: 'Cheap reach + viral upside (unboxing videos!). Volatile efficiency.',
  },
  {
    id: 'influencer',
    name: 'Influencer / PR',
    baseCac: 88,
    saturation: 650,
    fatiguePerDay: 0.002,
    recoveryPerDay: 0.03,
    brandBoost: 0.070,
    notes: 'Best brand-building dollar. Results lumpy; scams exist (fake reach).',
  },
  {
    id: 'amazonAds',
    name: 'Amazon Ads',
    baseCac: 102,
    saturation: 1000,
    fatiguePerDay: 0.003,
    recoveryPerDay: 0.06,
    brandBoost: 0.006,
    notes: 'Feeds the flywheel: sales velocity lifts organic rank. Zero brand equity.',
  },
  {
    id: 'email',
    name: 'Email / SMS / Retention',
    baseCac: 18,
    saturation: 260,
    fatiguePerDay: 0.000,
    recoveryPerDay: 0,
    brandBoost: 0.002,
    notes: 'Cheapest revenue you will ever touch — but capped by list size.',
  },
];

// ---------------------------------------------------------------------------
// Seasonality — retail calendar multipliers by day-of-year window.
// Anchored to Presidents Day / Memorial Day / July 4 / Labor Day /
// Black Friday-Cyber Monday promo cycles that dominate mattress retail.
// ---------------------------------------------------------------------------

export interface SeasonWindow {
  startDoy: number;
  endDoy: number;
  mult: number;
  label?: string;
}

// Day-of-year ranges (non-leap 2024 epoch; close enough across years).
export const SEASONALITY: SeasonWindow[] = [
  { startDoy: 0, endDoy: 9, mult: 1.18, label: 'New Year sleep reset' },
  { startDoy: 40, endDoy: 51, mult: 1.42, label: 'Presidents Day' },
  { startDoy: 138, endDoy: 149, mult: 1.34, label: 'Memorial Day' },
  { startDoy: 180, endDoy: 187, mult: 1.28, label: 'July 4th' },
  { startDoy: 195, endDoy: 215, mult: 0.9, label: 'Summer lull' },
  { startDoy: 239, endDoy: 250, mult: 1.4, label: 'Labor Day' },
  { startDoy: 275, endDoy: 295, mult: 0.92, label: 'October lull' },
  { startDoy: 327, endDoy: 334, mult: 1.72, label: 'Black Friday / Cyber Monday' },
  { startDoy: 341, endDoy: 355, mult: 1.1, label: 'Holiday shipping deadline' },
  { startDoy: 358, endDoy: 365, mult: 1.22, label: 'Year-end clearance' },
];

// ---------------------------------------------------------------------------
// Oil → cost pass-through curves.
//
// Foam/packaging are petrochemical-derived; ocean freight and parcel carry
// fuel surcharges that LAG spot moves by weeks. Healthy DTC brands keep
// all-in shipping at 8–12% of revenue; >15% is a five-alarm fire.
// ---------------------------------------------------------------------------

export const OIL_MEAN_BASE = 78; // long-run anchor ($/bbl), late-2020s regime
export const OIL_MIN = 42;
export const OIL_MAX = 135;

/** Freight surcharge multiplier from lagged WTI: $80/bbl ⇒ 1.00. */
export function freightMultFromOil(oilLagged: number): number {
  return 0.5 + oilLagged / 160;
}

/** Foam index drift target partially follows lagged oil (petro feedstock). */
export const FOAM_OIL_COUPLING = 0.3;

export const INBOUND_FREIGHT_PER_UNIT = {
  CN: 22,
  VN: 24,
  ID: 27,
  MX: 9,
  US: 4,
} as Record<CountryCode, number>;

export const HANDLING_PER_UNIT_INBOUND = 7;

export const RETURNS_REVERSE_LOGISTICS = 88; // pickup + inspection + disposal per returned unit
export const RETURNS_REFUND_SHARE = 1.0; // full refund policy (industry standard trials)

export const AMAZON_FBA_FULFILLMENT_PER_UNIT = 74; // oversized-item fulfillment blend
// BALANCE (v2): was 112 — with tariffed China landed costs, outbound parcel
// made starter-SKU DTC contribution margin ~$65/unit. $94 reflects scaled
// negotiated parcel rates (~17% of a $549 ASP, still above the 8–12%
// "healthy" benchmark — that benchmark is reachable at premium ASPs).
export const SHOPIFY_SHIP_PER_UNIT_BASE = 94; // parcel/LTL blend before fuel mult

// ---------------------------------------------------------------------------
// Inventory & ops
// ---------------------------------------------------------------------------

export const HOLDING_COST_PER_UNIT_PER_DAY = 0.055;
export const BASE_WAREHOUSE_CAPACITY = 1600; // units
export const CAPACITY_UPGRADE_COST_STEP = 45_000; // each expansion
export const CAPACITY_UPGRADE_STEP = 1600;

// ---------------------------------------------------------------------------
// Team — fixed-overhead hires with real operational effects.
// ---------------------------------------------------------------------------

export const TEAM_HIRES: TeamHire[] = [
  {
    id: 'cs-agent',
    name: 'Customer Sleep Coaches ×2',
    role: 'Support',
    dailyCost: 160,
    effectText: '-1.2pt return rate, faster review recovery',
    hired: false,
  },
  {
    id: 'supply-manager',
    name: 'Supply Chain Manager',
    role: 'Ops',
    dailyCost: 130,
    effectText: '-20% stockout rank damage, PO delay risk -30%',
    hired: false,
  },
  {
    id: 'growth-analyst',
    name: 'Growth Analyst',
    role: 'Marketing',
    dailyCost: 145,
    effectText: '+8% ad efficiency, slower creative fatigue',
    hired: false,
  },
  {
    id: 'engineer',
    name: 'Product Engineer',
    role: 'R&D',
    dailyCost: 170,
    effectText: '-20% R&D time on new products & research',
    hired: false,
  },
  {
    id: 'compliance',
    name: 'Trade Compliance Lead',
    role: 'Legal',
    dailyCost: 120,
    effectText: '-50% impact of customs/origin enforcement events',
    hired: false,
  },
];

export const BASE_OVERHEAD_DAILY = 420; // founder salary, tools, insurance, rent

// ---------------------------------------------------------------------------
// Finance
// ---------------------------------------------------------------------------

export const QUARTERLY_TAX_RATE = 0.24;
export const CREDIT_APR_DAILY = 0.115 / 365;
export const BANKRUPTCY_FLOOR = -60_000; // cash below this = game over

// ---------------------------------------------------------------------------
// Research tree. Costs in $, rDays in game days (engineer hire -20%).
// Effects are read by engines via `has(s, nodeId)` checks — see research.ts.
// ---------------------------------------------------------------------------

export const RESEARCH_TREE: ResearchNode[] = [
  // Tier 1 — survive & sharpen
  {
    id: 'compression-2',
    name: 'Advanced Compression Packaging',
    tier: 1,
    cost: 35_000,
    rDays: 25,
    prereqs: [],
    desc: 'Second-gen roll-pack reduces box volume another notch.',
    effectText: '-8% inbound freight, -4% outbound parcel cost',
  },
  {
    id: 'certipur',
    name: 'CertiPUR-US Certification',
    tier: 1,
    cost: 18_000,
    rDays: 14,
    prereqs: [],
    desc: 'Independent foam content & emissions certification.',
    effectText: '+6% Shopify conversion, unlocks trust badges, small press bump',
  },
  {
    id: 'service-playbook',
    name: 'White-Glove Service Playbook',
    tier: 1,
    cost: 22_000,
    rDays: 18,
    prereqs: [],
    desc: 'Structured 100-night trial support, pro active check-ins.',
    effectText: '-1.5pt returns, +reputation drift',
  },
  {
    id: 'global-sourcing',
    name: 'Global Sourcing Network',
    tier: 1,
    cost: 30_000,
    rDays: 21,
    prereqs: [],
    desc: 'Audited supplier relationships beyond China/Vietnam.',
    effectText: 'Unlocks Indonesia supplier; -15% tariff shock impact',
  },
  // Tier 2 — differentiate
  {
    id: 'hybrid-platform',
    name: 'Hybrid Coil Platform',
    tier: 2,
    cost: 65_000,
    rDays: 35,
    prereqs: ['compression-2'],
    desc: 'Pocket-coil engineering, steel sourcing contracts.',
    effectText: 'Unlocks Hybrid archetype',
  },
  {
    id: 'cooling-line',
    name: 'Cooling Gel & PCM Line',
    tier: 2,
    cost: 72_000,
    rDays: 38,
    prereqs: ['compression-2'],
    desc: 'Gel-infused foams + phase-change cover materials.',
    effectText: 'Unlocks Cooling Hybrid archetype; +cooling on future SKUs',
  },
  {
    id: 'brand-refresh',
    name: 'Brand System Refresh',
    tier: 2,
    cost: 48_000,
    rDays: 24,
    prereqs: ['certipur'],
    desc: 'New identity, packaging unboxing moment, creative studio.',
    effectText: '-10% all ad CAC, +8 awareness instantly, slower fatigue',
  },
  {
    id: 'forecasting',
    name: 'Demand Forecasting Suite',
    tier: 2,
    cost: 40_000,
    rDays: 22,
    prereqs: ['service-playbook'],
    desc: 'SKU-level velocity models wired to PO planning.',
    effectText: '-25% holding cost, low-stock warnings, +3% conversion',
  },
  {
    id: 'nearshore-lane',
    name: 'Nearshore Fast Lane (Mexico)',
    tier: 2,
    cost: 58_000,
    rDays: 30,
    prereqs: ['global-sourcing'],
    desc: 'Qualified Monterrey line + cross-dock logistics.',
    effectText: 'Unlocks Mexico supplier (13-day lead)',
  },
  // Tier 3 — scale
  {
    id: 'wholesale-program',
    name: 'Wholesale & Retail Program',
    tier: 3,
    cost: 95_000,
    rDays: 40,
    prereqs: ['hybrid-platform', 'certipur'],
    desc: 'Regional retailer network, EDI, floor-model programs.',
    effectText: 'Unlocks Wholesale channel (bulk orders, net-60 terms)',
  },
  {
    id: 'latex-line',
    name: 'Natural Latex Line',
    tier: 3,
    cost: 88_000,
    rDays: 42,
    prereqs: ['cooling-line'],
    desc: 'Talalay/Dunlop supply agreements, eco certifications.',
    effectText: 'Unlocks Latex archetype; eco-segment demand bonus',
  },
  {
    id: 'in-house-machinery',
    name: 'In-House Compression Line',
    tier: 3,
    cost: 130_000,
    rDays: 50,
    prereqs: ['hybrid-platform'],
    desc: 'Own roll-pack machinery + QC lab.',
    effectText: '-9% COGS on all SKUs, defect rate -40%',
  },
  {
    id: 'showroom-pilot',
    name: 'Showroom Pilot Program',
    tier: 3,
    cost: 150_000,
    rDays: 45,
    prereqs: ['wholesale-program'],
    desc: 'Qualify your first physical sleep shop. Then scale locations and remodel tiers.',
    effectText: 'Unlocks Showrooms: up to 4 locations, +5–13% demand each, wholesale boost, rep halo',
  },
  // Tier 4 — empire
  {
    id: 'reshore-pilot',
    name: 'Reshored US Production',
    tier: 4,
    cost: 210_000,
    rDays: 60,
    prereqs: ['in-house-machinery'],
    desc: 'Domestic assembly cell. Trade-war immunity as a feature.',
    effectText: 'Unlocks USA supplier; "Made in USA" conversion boost',
  },
  {
    id: 'international',
    name: 'International Expansion (CA/EU)',
    tier: 4,
    cost: 185_000,
    rDays: 55,
    prereqs: ['wholesale-program'],
    desc: 'Maple-crossing and Atlantic freight programs, VAT handling.',
    effectText: '+18% global demand pool, FX/regulatory risk events enabled',
  },
  {
    id: 'vertical-foam',
    name: 'Vertical Foam Pour Plant',
    tier: 4,
    cost: 320_000,
    rDays: 70,
    prereqs: ['reshore-pilot'],
    desc: 'Own polyol blending. Commodity exposure becomes your moat.',
    effectText: '-14% COGS, foamIndex shocks halved',
  },
];

export const MAX_PRODUCTS = 6;

// ---------------------------------------------------------------------------
// Achievements
// ---------------------------------------------------------------------------

export interface AchievementDef {
  id: string;
  name: string;
  desc: string;
}

export const ACHIEVEMENTS: AchievementDef[] = [
  { id: 'first-sale', name: 'First Zzz', desc: 'Sell your first mattress.' },
  { id: 'first-100k', name: 'Six Figures', desc: 'Reach $100k lifetime revenue.' },
  { id: 'first-1m', name: 'Ramp It Up', desc: '$1M lifetime revenue.' },
  { id: 'first-10m', name: 'Real Empire', desc: '$10M lifetime revenue.' },
  { id: 'profitable-quarter', name: 'Actually Profitable', desc: 'Close a quarter net-income positive.' },
  { id: 'tariff-survivor', name: 'Tariff Whiplash Survivor', desc: 'Stay solvent through a major trade shock.' },
  { id: 'oil-crisis', name: 'Freight Storm Rider', desc: 'Endure an oil spike above $110 with positive quarterly net.' },
  { id: 'empire-exit', name: 'Signed the Term Sheet', desc: 'Accept an acquisition offer.' },
];

// ---------------------------------------------------------------------------
// Event category styling (UI)
// ---------------------------------------------------------------------------

export const EVENT_CATEGORY_META: Record<EventCategory | 'finance' | 'milestone', { label: string }> = {
  trade: { label: 'Trade & Tariffs' },
  macro: { label: 'Macro & Commodities' },
  competition: { label: 'Competition' },
  platform: { label: 'Platform Policy' },
  viral: { label: 'Viral / Social' },
  supply: { label: 'Supply Chain' },
  regulatory: { label: 'Regulatory' },
  opportunity: { label: 'Opportunity' },
  positive: { label: 'Good News' },
  finance: { label: 'Finance' },
  milestone: { label: 'Milestone' },
};

// ---------------------------------------------------------------------------
// Misc gameplay tuning
// ---------------------------------------------------------------------------

export const TUNING = {
  /** Awareness decay per day (brand memory fades without spend). */
  awarenessDecayPerDay: 0.015,
  reputationDriftTarget: 62,
  reputationDriftRate: 0.01,
  /** Amazon rank decay when out of stock or idle. */
  amazonRankIdleDecay: 0.35,
  amazonRankOosDecay: 1.6,
  /** Organic Shopify sessions per point of awareness per day (pre-modifiers). */
  shopifyOrganicPerAwareness: 0.9,
  /** Returning-customer reorder share pulled back into Shopify demand/day. */
  returningShare: 0.004,
  /** Review count growth per sale (review propensity). */
  reviewsPerSale: 0.05,
  /** Viral chance per day when TikTok spend exceeds threshold. */
  tiktokViralChancePerDay: 0.006,
  tiktokViralMinSpend: 250,
  /** Exit offers begin after this day and recur at interval. */
  exitOfferFirstDay: 545,
  exitOfferIntervalDays: 180,
  /** Offline catch-up cap (days simulated while away). */
  offlineMaxDays: 21,
} as const;

// ---------------------------------------------------------------------------
// Showrooms — the retail halo program (unlocked via `showroom-pilot`).
// Each physical location adds local demand + brand trust at painful fixed
// cost. Tiers are per-location remodels.
// ---------------------------------------------------------------------------

export interface ShowroomCityDef {
  city: string;
  blurb: string;
}

export const SHOWROOM_CITIES: ShowroomCityDef[] = [
  { city: 'Austin, TX', blurb: 'Sleep-tech hub. Influencer-dense, rent reasonable.' },
  { city: 'Denver, CO', blurb: 'Active recovery crowd. Strong premium hybrid demand.' },
  { city: 'Charlotte, NC', blurb: 'Furniture country. Wholesale neighbors, value shoppers.' },
  { city: 'Scottsdale, AZ', blurb: 'Luxury market. Latex and cooling lines shine here.' },
];

export const SHOWROOM_MAX_LOCATIONS = 4;
export const SHOWROOM_OPEN_COST = 120_000;
export const SHOWROOM_UPGRADE_COST: Record<2 | 3, number> = { 2: 60_000, 3: 110_000 };
/** Daily lease + staffing cost by tier. */
export const SHOWROOM_DAILY_COST: Record<1 | 2 | 3, number> = { 1: 450, 2: 700, 3: 1200 };
/** Demand multiplier bonus contributed per location by tier. */
export const SHOWROOM_DEMAND_BONUS: Record<1 | 2 | 3, number> = { 1: 0.05, 2: 0.09, 3: 0.13 };
export const SHOWROOM_DEMAND_CAP = 0.4;
/** Wholesale partners gained per showroom location. */
export const SHOWROOM_WHOLESALE_BONUS = 0.12;
